import type { configType, callbackFntype, callbackEventType } from './types';
import {
	initDOMNodeWithOverlay,
	prepareCloseModalNode,
	sanityCheck,
	importFonts,
	getTime,
} from './helper';

const appDomain = 'https://onramp.money';
const sandboxDomain = 'https://test.onramp.money/onramp';
const path = '/main';

var startTime: any, endTime: any;

export class OnrampWebSDK {
	_appURL: string;
	_isVisible: boolean;
	_originalBodyOverflow: string | null = null;
	_clientCallbacks: { [key: string]: callbackFntype } = {};
	config: configType;
	_domNodes!: {
		body: HTMLBodyElement | null;
		iframe: HTMLIFrameElement;
		overlay: HTMLDivElement | null;
		shadow: ShadowRoot | null;
		shadowHost: HTMLDivElement | null;
		container: Element | null;
	};
	constructor(config: configType) {
		this._appURL = `${config?.sandbox === true ? sandboxDomain : appDomain}${path}`;
		this._isVisible = false;
		this.config = config;
		importFonts();
	}

	async _getURL(): Promise<string> {
		const {
			appId,
			flowType = 1,
			sandbox,
			containerId,
			theme,
			...rest
		} = this.config;

		const mode = containerId ? 'embedded' : 'overlay';
		const container = containerId
			? (document.querySelector(containerId) as HTMLElement)
			: null;
		const clientHeight =
			container?.getBoundingClientRect().height || window.innerHeight;

		const url = new URL('https://api.onramp.money/sdk-apis/generate-url');
		const params = url.searchParams;

		// Required params
		params.append('appURL', String(this._appURL));
		params.append('appId', String(appId));
		params.append('flowType', String(flowType));
		params.append('mode', mode);
		params.append('origin', window.location.origin);
		params.append('clientHeight', String(clientHeight));

		if (sandbox !== undefined) {
			params.append('sandbox', String(sandbox));
		}

		// Append all other config values dynamically
		for (const [key, value] of Object.entries(rest)) {
			if (value !== undefined && value !== null && value !== '') {
				params.append(key, String(value));
			}
		}

		const response = await fetch(url.href);
		if (!response.ok) {
			throw new Error(
				`Failed to generate widget URL: ${response.status}`
			);
		}
		const json = await response.json();
		if (!json.data?.url) {
			throw new Error('Invalid response from URL generation API');
		}
		return json.data.url;
	}

	_registerSDKEvents() {
		window.addEventListener('message', this._widgetMessageHandler);
		window.addEventListener('keydown', this._handleEscapeClick);
		window.addEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST',
			this._widgetCloseRequestHandler
		);
		window.addEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST_CONFIRMED',
			this._widgetCloseHandler
		);
		window.addEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST_CANCELLED',
			this._widgetCloseCancelHandler
		);
		window.addEventListener(
			'ONRAMP_WIDGET_READY',
			this._widgetReadyHandler
		);
		window.addEventListener(
			'ONRAMP_WIDGET_FAILED',
			this._widgetFailedHandler
		);
	}

	_teardownSDKEvents() {
		window.removeEventListener('message', this._widgetMessageHandler);
		window.removeEventListener('keydown', this._handleEscapeClick);
		window.removeEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST',
			this._widgetCloseRequestHandler
		);
		window.removeEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST_CONFIRMED',
			this._widgetCloseHandler
		);
		window.removeEventListener(
			'ONRAMP_WIDGET_CLOSE_REQUEST_CANCELLED',
			this._widgetCloseCancelHandler
		);
		window.removeEventListener(
			'ONRAMP_WIDGET_READY',
			this._widgetReadyHandler
		);
		window.removeEventListener(
			'ONRAMP_WIDGET_FAILED',
			this._widgetFailedHandler
		);
	}

	_handleEscapeClick = (event: { key: string; keyCode: number }) => {
		const escKeyCode = 27;
		if (
			event.key === 'Escape' ||
			event.key === 'Esc' ||
			event.keyCode === escKeyCode
		) {
			const customEvent = new CustomEvent('ONRAMP_WIDGET_CLOSE_REQUEST', {
				detail: { eventData: { isOnramp: true } },
			});
			window.dispatchEvent(customEvent);
		}
	};

	_widgetMessageHandler = (e: callbackEventType) => {
		if (e?.data?.eventData?.isOnramp) {
			const { eventData } = e.data;
			// TODO: add sanity check for event type

			// why do we send custom event?
			const customEvent = new CustomEvent(eventData.type, {
				detail: eventData,
			});
			window.dispatchEvent(customEvent);

			// also calls any callbacks registered by sdk instance
			if (eventData?.type.startsWith('ONRAMP_WIDGET_TX_')) {
				this._clientCallbacks?.['TX_EVENTS']?.(eventData);
			} else if (
				[
					'ONRAMP_WIDGET_CLOSE_REQUEST_CONFIRMED',
					'ONRAMP_WIDGET_READY',
					'ONRAMP_WIDGET_FAILED',
					'ONRAMP_WIDGET_CONTENT_COPIED',
				].includes(eventData?.type)
			) {
				this._clientCallbacks?.['WIDGET_EVENTS']?.(eventData);
			} else if (eventData?.type.startsWith('ONRAMP_KYC_')) {
				this._clientCallbacks?.['KYC_EVENTS']?.(eventData);
			} else {
				console.warn(
					'unhandled event received from === ',
					e?.origin,
					e
				);
			}
		} else {
			console.log('event received from === ', e?.origin, e);
		}
	};

	_widgetCloseRequestHandler = () => {
		// Only show close modal in shadow DOM mode
		if (this._domNodes?.overlay) {
			const modal = this._domNodes.overlay.querySelector('.close-modal');
			if (!modal) {
				this._domNodes.overlay.appendChild(prepareCloseModalNode());
			}
		}
	};

	_widgetCloseHandler = () => {
		this._teardownSDKEvents();
		this.close();
	};

	_widgetCloseCancelHandler = () => {
		// Only handle modal in shadow DOM mode
		if (this._domNodes?.overlay) {
			const modal = this._domNodes.overlay.querySelector('.close-modal');
			if (modal) {
				modal.remove();
			}
		}
	};

	_widgetReadyHandler = () => {
		// Handle loader removal only in shadow DOM mode
		if (this._domNodes?.overlay) {
			this._domNodes.overlay.querySelector('.loader-container')?.remove();
		}
		this._domNodes?.iframe?.classList?.add('visible');
		endTime = getTime();
		if (startTime && endTime) {
			console.log(
				'Render time in seconds ~~ ',
				Number((endTime - startTime) / 1000).toPrecision(4)
			);
		}
	};

	_widgetFailedHandler = () => {
		// this._teardownSDKEvents();
		// this.close();
	};

	close = () => {
		if (this._isVisible) {
			if (this._domNodes?.shadowHost) {
				// Shadow DOM mode cleanup
				this._domNodes.shadowHost.remove();
				if (this._domNodes.body) {
					this._domNodes.body.style.overflow =
						this._originalBodyOverflow || '';
				}
			} else if (this._domNodes?.container) {
				// Container mode cleanup
				const iframe =
					this._domNodes.container.querySelector('#onRampWidget');
				if (iframe) {
					iframe.remove();
				}
			}
			this._isVisible = false;
		} else {
			throw new Error(
				'no widget to close, please call show method first'
			);
		}
	};

	show = async () => {
		startTime = getTime();
		const dataSanityCheck = sanityCheck(this.config);
		if (dataSanityCheck) {
			if (this._isVisible) {
				throw new Error(
					'Widget is already rendered. Call close method to clear it up'
				);
			}
			this._registerSDKEvents();
			const widgetUrl = await this._getURL();
			this._domNodes = initDOMNodeWithOverlay(
				widgetUrl,
				this.config?.theme,
				this.config?.containerId
			);
			// Only append shadowHost if in shadow DOM mode
			if (this._domNodes.shadowHost) {
				this._domNodes.body?.appendChild(this._domNodes.shadowHost);
				if (this._domNodes.body) {
					this._originalBodyOverflow =
						this._domNodes.body?.style?.overflow || '';
					this._domNodes.body.style.overflow = 'hidden';
				}
			}
			this._isVisible = true;
			setTimeout(() => {
				this._widgetReadyHandler();
			}, 1000);
		} else {
			return dataSanityCheck;
		}
	};

	on = (eventType: string, callbackFn: callbackFntype) => {
		if (['TX_EVENTS', 'WIDGET_EVENTS', 'KYC_EVENTS'].includes(eventType)) {
			this._clientCallbacks[eventType] = callbackFn;
		}
	};
}
